import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  useLocation,
  useNavigate,
  useOutlet,
  useParams,
} from 'react-router-dom'
import { Alert } from '@/ui/Alert/Alert'
import { Button } from '@/ui/Button/Button'
import { Card } from '@/ui/Card/Card'
import { Chip } from '@/ui/Chip/Chip'
import { SelectField, TextField } from '@/ui/Field/Field'
import { Skeleton } from '@/ui/Loading/Loading'
import { useSkeletonDelay } from '@/ui/Loading/useSkeletonDelay'
import { Inline } from '@/ui/layout/Inline'
import { Stack } from '@/ui/layout/Stack'
import { Text } from '@/ui/layout/Text'
import { SectionHeader } from '@/ui/SectionHeader/SectionHeader'
import { agentDefsApi, agentTemplatesApi, providersApi } from '../api/agents'
import { ApiError, type ApiResponse, NetworkError } from '../api/client'
import { topicsApi } from '../api/topics'
import {
  BLANK,
  fromDef,
  fromTemplate,
  toTemplateBody,
} from '../components/agent-editor/serialization'
import { AgentV2Editor } from '../components/agent-v2/AgentV2Editor'
import { type Crumb, WsPageShell } from '../components/WsPageShell'
import { ROUTES } from '../constants/routes'
import { agentEditorCopy } from '../copy'
import {
  agentQueryKeys,
  topicQueryKeys,
  useAgentTemplatesQuery,
  useMeQuery,
  useTopicQuery,
} from '../hooks/queries'
import { useGoBack } from '../hooks/useGoBack'
import { useToastStore } from '../store/toast'
import { useWorkspaceStore } from '../store/workspace'
import type {
  AgentDefInfo,
  AgentTemplate,
  AvailableProviderInfo,
  CreateAgentDefBody,
  CreateAgentDefDraftBody,
  TopicInfo,
  UpdateAgentDefBody,
} from '../types/api'
import type { FormState } from '../types/editor'
import { canonicalJsonEqual } from '../utils/canonicalJson'
import {
  parseReturnTo,
  type ValidatedReturnTo,
  withValidatedReturnTo,
} from '../utils/returnTo'

function newCreationRequestId(): string {
  if (typeof globalThis.crypto?.randomUUID === 'function') {
    return globalThis.crypto.randomUUID()
  }
  throw new Error(agentEditorCopy.form.requestIdUnavailable)
}

function isAmbiguousCreateError(error: unknown): boolean {
  return (
    error instanceof NetworkError ||
    (error instanceof ApiError && error.status >= 500 && !error.isStructured)
  )
}

function createDraftMatchesFrozen(
  frozen: CreateAgentDefBody,
  draft: CreateAgentDefDraftBody,
): boolean {
  const frozenDraft = Object.fromEntries(
    Object.entries(frozen).filter(([key]) => key !== 'creation_request_id'),
  )
  return canonicalJsonEqual(frozenDraft, draft)
}

interface TopicAgentOrigin {
  pathname: string
  search: string
  topicId: string
  workspaceId: string
  href: string
}

interface AssignmentSubmission {
  agent: AgentDefInfo
  payload: { add: string[]; remove: string[] }
  status: 'assigning' | 'unknown'
}

function topicReturnTarget(target: ValidatedReturnTo | null, isNew: boolean) {
  return isNew && target?.kind === 'topic' ? target : null
}

function isTopicUnavailable(error: unknown): boolean {
  return (
    error instanceof ApiError && (error.status === 403 || error.status === 404)
  )
}

// ── Save-as-template section ───────────────────────────────────────────────────

/**
 * Opened by the editor's "Save as template" button and rendered directly below
 * it, so the form stays on the page instead of behind a dialog.
 */
function SaveTemplateSection({
  initialName,
  isPending,
  onSave,
  onClose,
}: {
  initialName: string
  isPending: boolean
  onSave: (name: string, description: string) => void
  onClose: () => void
}) {
  const [name, setName] = useState(initialName)
  const [description, setDescription] = useState('')
  const ref = useRef<HTMLElement>(null)

  // The section mounts below the fold; bring it into view so the click on the
  // trigger doesn't appear to do nothing.
  useEffect(() => {
    ref.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
  }, [])

  return (
    <Card ref={ref}>
      <Stack gap="space-16">
        <SectionHeader as="h2">
          {agentEditorCopy.form.saveTemplateHeading}
        </SectionHeader>
        <TextField
          id="tpl-name"
          label={agentEditorCopy.form.nameLabel}
          value={name}
          onValue={setName}
          inputProps={{ autoFocus: true }}
        />
        <TextField
          id="tpl-desc"
          label={agentEditorCopy.description}
          hint={agentEditorCopy.form.optionalHint}
          value={description}
          onValue={setDescription}
        />
        <Inline gap="space-8" justify="end">
          <Button
            variant="outline"
            ground="surface"
            onClick={onClose}
            disabled={isPending}
          >
            {agentEditorCopy.form.cancel}
          </Button>
          <Button
            variant="outline"
            ground="surface"
            disabled={!name.trim()}
            onClick={() => onSave(name.trim(), description.trim())}
            loading={isPending}
          >
            {agentEditorCopy.form.saveTemplate}
          </Button>
        </Inline>
      </Stack>
    </Card>
  )
}

// ── Template card ──────────────────────────────────────────────────────────────

function TemplateCard({
  templates,
  selectedId,
  onSelect,
  loading,
  error,
  onRetry,
}: {
  templates: AgentTemplate[]
  /** Currently applied template; undefined means Blank. */
  selectedId?: string
  onSelect: (t: AgentTemplate | null) => void
  loading: boolean
  error: boolean
  onRetry: () => void
}) {
  const showSkeleton = useSkeletonDelay(loading)
  const selected = templates.find((t) => t.id === selectedId)

  if (loading) {
    return (
      <Card>
        <Stack gap="space-8" aria-busy="true">
          {showSkeleton && <Skeleton lines={3} ground="surface" />}
        </Stack>
      </Card>
    )
  }

  if (error) {
    return (
      <Card>
        <Alert tone="danger">
          <Inline gap="space-8" align="baseline">
            <Text>{agentEditorCopy.loadTemplatesFailed}</Text>
            <Button variant="outline" size="sm" onClick={onRetry}>
              {agentEditorCopy.retry}
            </Button>
          </Inline>
        </Alert>
      </Card>
    )
  }

  return (
    <Card>
      <SelectField
        id="af-template"
        label={agentEditorCopy.form.templateLabel}
        value={selectedId ?? ''}
        onValue={(id) => onSelect(templates.find((t) => t.id === id) ?? null)}
        help={
          selected ? (
            // Field renders help inside a <p>, so no block-level Inline here.
            <>
              {selected.id.startsWith('builtin:') && (
                <>
                  <Chip
                    variant="kind"
                    label={agentEditorCopy.form.builtIn}
                  />{' '}
                </>
              )}
              {selected.description && <>{selected.description} </>}
              <Text variant="code">{selected.model}</Text>
            </>
          ) : undefined
        }
      >
        <option value="">{agentEditorCopy.blankTemplate}</option>
        {templates.map((t) => (
          <option key={t.id} value={t.id}>
            {t.name}
          </option>
        ))}
      </SelectField>
    </Card>
  )
}

// ── Main page ──────────────────────────────────────────────────────────────────

export function AgentFormPage() {
  const { agentId, workspaceId } = useParams<{
    agentId: string
    workspaceId: string
  }>()
  const location = useLocation()
  const navigationState = location.state as {
    agent?: AgentDefInfo
    wsId?: string
    /** Save/cancel fallback destination (e.g. `/admin` from the admin panel). */
    returnTo?: string
    /** Template to preselect, passed by the "Use template" affordances. */
    template?: AgentTemplate
  } | null
  // Nested prompt navigation replaces the current history entry and therefore
  // has a new location object. Capture launch state once so the selected
  // agent/template and caller survive that transition.
  const [initialNavigationState] = useState(navigationState)
  const activeWorkspaceId = useWorkspaceStore((s) => s.activeWorkspaceId)
  const qc = useQueryClient()
  const navigate = useNavigate()
  const addToast = useToastStore((s) => s.addToast)
  const isNew = !agentId
  const fallbackWorkspaceId =
    workspaceId ?? initialNavigationState?.wsId ?? activeWorkspaceId ?? ''
  const [returnTarget] = useState<ValidatedReturnTo | null>(() => {
    const serialized = new URLSearchParams(location.search).get('return_to')
    return (
      parseReturnTo(serialized) ??
      parseReturnTo(initialNavigationState?.returnTo)
    )
  })
  const requestedTopicOrigin = topicReturnTarget(returnTarget, isNew)
  const originTopicQuery = useTopicQuery(requestedTopicOrigin?.topicId, {
    enabled: !!requestedTopicOrigin,
  })
  const originWorkspaceId = originTopicQuery.data?.workspace_id
  const topicOrigin = useMemo<TopicAgentOrigin | null>(
    () =>
      requestedTopicOrigin && originWorkspaceId
        ? {
            pathname: requestedTopicOrigin.pathname,
            search: requestedTopicOrigin.search,
            topicId: requestedTopicOrigin.topicId,
            workspaceId: originWorkspaceId,
            href: requestedTopicOrigin.href,
          }
        : null,
    [originWorkspaceId, requestedTopicOrigin],
  )

  const wsId = requestedTopicOrigin
    ? (topicOrigin?.workspaceId ?? originTopicQuery.data?.workspace_id ?? '')
    : fallbackWorkspaceId
  const returnTo =
    returnTarget?.href ?? ROUTES.workspaceSettings(wsId || fallbackWorkspaceId)
  const { data: me } = useMeQuery()
  const wsName =
    me?.workspaces.find((w) => w.workspace_id === wsId)?.name ??
    agentEditorCopy.form.workspaceCrumb
  // Back goes to the actual previous screen (console, admin, etc.) when there's
  // history; only direct-URL loads with no prior entry fall back to the
  // caller-provided returnTo (or the workspace settings), matching every other
  // form page's useGoBack behavior.
  const goBack = useGoBack(returnTo)

  const [apiError, setApiError] = useState<string | null>(null)
  // Lives here rather than in the editor so the save-template mutation can keep
  // the section (and the user's typed name) up until the save actually lands.
  const [showSaveTemplate, setShowSaveTemplate] = useState(false)
  // Incrementing key forces the editor to re-mount when a template is applied.
  const [editorKey, setEditorKey] = useState(0)
  // Seeded from navigation state so "Use template" lands with the template
  // already applied.
  const [appliedTemplate, setAppliedTemplate] = useState<AgentTemplate | null>(
    initialNavigationState?.template ?? null,
  )
  // The first key belongs to the form instance, not the submit click, so it
  // survives nested prompt navigation and any pre-submit editing.
  const [initialCreationRequestId] = useState(() =>
    isNew ? newCreationRequestId() : null,
  )
  const frozenCreateBody = useRef<CreateAgentDefBody | null>(null)
  const createRetryTimer = useRef<
    ReturnType<typeof globalThis.setTimeout> | undefined
  >(undefined)
  const [createSubmission, setCreateSubmission] = useState<{
    status: 'pending' | 'unknown' | 'confirmed'
  } | null>(null)
  const [assignmentSubmission, setAssignmentSubmission] =
    useState<AssignmentSubmission | null>(null)
  const updateRetryTimer = useRef<
    ReturnType<typeof globalThis.setTimeout> | undefined
  >(undefined)
  const [updatePending, setUpdatePending] = useState(false)

  useEffect(
    () => () => {
      if (createRetryTimer.current) {
        globalThis.clearTimeout(createRetryTimer.current)
      }
      if (updateRetryTimer.current) {
        globalThis.clearTimeout(updateRetryTimer.current)
      }
    },
    [],
  )

  const {
    data: fetchedAgent,
    isLoading: agentLoading,
    isError: agentIsError,
    error: agentError,
    refetch: refetchAgent,
  } = useQuery({
    queryKey: agentQueryKeys.detail(wsId, agentId ?? ''),
    queryFn: () =>
      // biome-ignore lint/style/noNonNullAssertion: route param guaranteed by router
      agentDefsApi.get(wsId, agentId!),
    enabled: !isNew && !initialNavigationState?.agent && !!wsId && !!agentId,
    staleTime: 30_000,
  })

  const { data: providers = [] } = useQuery<AvailableProviderInfo[]>({
    queryKey: ['providers', wsId],
    queryFn: () => providersApi.listAvailable(wsId || undefined),
    enabled: !!wsId,
    staleTime: 300_000,
  })

  const {
    data: templates = [],
    isLoading: templatesLoading,
    isError: templatesError,
    refetch: refetchTemplates,
  } = useAgentTemplatesQuery(wsId, isNew && !!wsId)

  const agent: AgentDefInfo | null =
    initialNavigationState?.agent ?? fetchedAgent ?? null

  const createMutation = useMutation({
    mutationFn: (body: CreateAgentDefBody) =>
      agentDefsApi.createWithMeta(wsId, body),
    onSuccess: (response) => {
      if (response.data) {
        qc.setQueryData(
          agentQueryKeys.detail(wsId, response.data.agent_id),
          response.data,
        )
      }
      if (response.status === 202) {
        setCreateSubmission({
          status: 'pending',
        })
        const delay = Math.max(250, (response.retryAfterSeconds ?? 1) * 1_000)
        createRetryTimer.current = globalThis.setTimeout(() => {
          createRetryTimer.current = undefined
          const body = frozenCreateBody.current
          if (body) createMutation.mutate(body)
        }, delay)
        return
      }
      if (!response.data) {
        setCreateSubmission({
          status: 'unknown',
        })
        return
      }
      frozenCreateBody.current = null
      setCreateSubmission(topicOrigin ? null : { status: 'confirmed' })
      void handleConfirmedCreate(response.data)
    },
    onError: (error: Error) => {
      if (isAmbiguousCreateError(error)) {
        setCreateSubmission({
          status: 'unknown',
        })
        return
      }
      setCreateSubmission(null)
      setApiError(error.message)
    },
  })

  function handleUpdateResponse(response: ApiResponse<AgentDefInfo>) {
    qc.setQueryData(
      agentQueryKeys.detail(wsId, response.data.agent_id),
      response.data,
    )
    void qc.invalidateQueries({ queryKey: ['agents', wsId] })
    if (response.status === 202) {
      setUpdatePending(true)
      const delay = Math.max(250, (response.retryAfterSeconds ?? 1) * 1_000)
      updateRetryTimer.current = globalThis.setTimeout(() => {
        updateRetryTimer.current = undefined
        reconcileMutation.mutate()
      }, delay)
      return
    }
    setUpdatePending(false)
    goBack()
  }

  const updateMutation = useMutation({
    mutationFn: (body: UpdateAgentDefBody) =>
      // biome-ignore lint/style/noNonNullAssertion: route param guaranteed by router
      agentDefsApi.updateWithMeta(wsId, agentId!, body),
    onSuccess: handleUpdateResponse,
    onError: (e: Error) => setApiError(e.message),
  })

  const reconcileMutation = useMutation({
    mutationFn: () =>
      // biome-ignore lint/style/noNonNullAssertion: route param guaranteed by router
      agentDefsApi.reconcileWithMeta(wsId, agentId!),
    onSuccess: handleUpdateResponse,
    onError: (e: Error) => setApiError(e.message),
  })

  // v2 header toggle: lifecycle changes must be submitted separately from
  // configuration updates (the server rejects mixed PATCHes), so this
  // rides its own path rather than the editor's submit. A plain function:
  // the guard inside makes double-fires harmless and no consumer needs a
  // stable identity.
  const [pausePending, setPausePending] = useState(false)
  async function togglePause() {
    if (!agentId || pausePending) return
    setPausePending(true)
    setApiError(null)
    try {
      const next = agent?.status === 'paused' ? 'active' : 'paused'
      const response = await agentDefsApi.updateWithMeta(wsId, agentId, {
        status: next,
      })
      qc.setQueryData(agentQueryKeys.detail(wsId, agentId), response.data)
      void qc.invalidateQueries({ queryKey: ['agents', wsId] })
    } catch (e) {
      setApiError(
        e instanceof Error ? e.message : agentEditorCopy.form.lifecycleFailed,
      )
    } finally {
      setPausePending(false)
    }
  }

  const saveTemplateMutation = useMutation({
    mutationFn: (body: Parameters<typeof agentTemplatesApi.create>[1]) =>
      agentTemplatesApi.create(wsId, body),
    onSuccess: (_, vars) => {
      void qc.invalidateQueries({ queryKey: ['agent-templates', wsId] })
      setShowSaveTemplate(false)
      addToast(agentEditorCopy.form.templateSaved(vars.name), 'success')
    },
    onError: (e: Error) => setApiError(e.message),
  })

  async function navigateToConfirmedOrigin(origin: TopicAgentOrigin) {
    setAssignmentSubmission(null)
    await Promise.all([
      qc.invalidateQueries({
        queryKey: topicQueryKeys.detail(origin.topicId),
      }),
      qc.invalidateQueries({
        queryKey: topicQueryKeys.workspace(origin.workspaceId),
      }),
    ])
    void navigate(origin.href, { replace: true })
  }

  function handleTopicRefetchFailure(
    origin: TopicAgentOrigin,
    agent: AgentDefInfo,
    error: unknown,
  ) {
    const unavailable = isTopicUnavailable(error)
    addToast(
      unavailable
        ? agentEditorCopy.status.topicUnavailable
        : agentEditorCopy.status.topicUnchecked,
      unavailable ? 'error' : 'info',
    )
    setAssignmentSubmission(null)
    void navigate(ROUTES.agentProfile(origin.workspaceId, agent.agent_id), {
      replace: true,
    })
  }

  async function recoverTopicAssignment(
    origin: TopicAgentOrigin,
    agent: AgentDefInfo,
    payload: AssignmentSubmission['payload'],
    outcome: 'rejected' | 'unknown',
    error?: unknown,
  ) {
    let fresh: TopicInfo
    try {
      fresh = await topicsApi.get(origin.topicId, { noCache: true })
    } catch (refetchError) {
      handleTopicRefetchFailure(origin, agent, refetchError)
      return
    }

    qc.setQueryData(topicQueryKeys.detail(origin.topicId), fresh)
    void qc.invalidateQueries({
      queryKey: topicQueryKeys.workspace(fresh.workspace_id),
    })
    if (fresh.agents.some((member) => member.agent_id === agent.agent_id)) {
      await navigateToConfirmedOrigin(origin)
      return
    }

    if (outcome === 'rejected') {
      addToast(
        agentEditorCopy.status.topicAddFailed(
          error instanceof Error ? error.message : undefined,
        ),
        'error',
      )
      setAssignmentSubmission(null)
      void navigate(ROUTES.topicSettings(origin.topicId), { replace: true })
      return
    }

    setAssignmentSubmission({
      agent,
      payload,
      status: 'unknown',
    })
  }

  async function performTopicAssignment(
    origin: TopicAgentOrigin,
    agent: AgentDefInfo,
    payload: AssignmentSubmission['payload'],
  ) {
    setAssignmentSubmission({
      agent,
      payload,
      status: 'assigning',
    })

    try {
      await topicsApi.updateAgents(origin.topicId, payload)
    } catch (firstError) {
      if (!isAmbiguousCreateError(firstError)) {
        await recoverTopicAssignment(
          origin,
          agent,
          payload,
          'rejected',
          firstError,
        )
        return
      }

      // The add is an idempotent desired delta. A transport/unstructured
      // outcome gets one deliberate exact retry before any snapshot is used as
      // evidence.
      try {
        await topicsApi.updateAgents(origin.topicId, payload)
      } catch (retryError) {
        await recoverTopicAssignment(
          origin,
          agent,
          payload,
          isAmbiguousCreateError(retryError) ? 'unknown' : 'rejected',
          retryError,
        )
        return
      }
    }

    await navigateToConfirmedOrigin(origin)
  }

  async function handleConfirmedCreate(created: AgentDefInfo) {
    const payload = { add: [created.agent_id], remove: [] }
    if (topicOrigin) {
      setAssignmentSubmission({
        agent: created,
        payload,
        status: 'assigning',
      })
    }
    await qc.invalidateQueries({
      queryKey: ['agents', wsId],
    })
    if (
      returnTarget?.kind === 'resource' ||
      returnTarget?.kind === 'workspace-recipes'
    ) {
      // Back where the agent was asked for (a recipe's setup), with the new
      // agent picked.
      const next = new URL(returnTarget.href, window.location.origin)
      next.searchParams.set('agent', created.agent_id)
      void navigate(`${next.pathname}${next.search}`, { replace: true })
      return
    }
    if (!topicOrigin) {
      goBack()
      return
    }
    await performTopicAssignment(topicOrigin, created, payload)
  }

  async function retryTopicAssignment() {
    const current = assignmentSubmission
    if (!topicOrigin || !current || current.status !== 'unknown') return

    setAssignmentSubmission({
      ...current,
      status: 'assigning',
    })
    try {
      const fresh = await topicsApi.get(topicOrigin.topicId, { noCache: true })
      qc.setQueryData(topicQueryKeys.detail(topicOrigin.topicId), fresh)
      if (
        fresh.agents.some(
          (member) => member.agent_id === current.agent.agent_id,
        )
      ) {
        await navigateToConfirmedOrigin(topicOrigin)
        return
      }
    } catch (error) {
      handleTopicRefetchFailure(topicOrigin, current.agent, error)
      return
    }

    await performTopicAssignment(topicOrigin, current.agent, current.payload)
  }

  function handleSubmit(body: CreateAgentDefDraftBody | UpdateAgentDefBody) {
    setApiError(null)
    if (isNew) {
      const draft = body as CreateAgentDefDraftBody
      const previous = frozenCreateBody.current
      const frozen =
        previous && createDraftMatchesFrozen(previous, draft)
          ? previous
          : ({
              ...draft,
              creation_request_id: previous
                ? newCreationRequestId()
                : (initialCreationRequestId ?? newCreationRequestId()),
            } satisfies CreateAgentDefBody)
      frozenCreateBody.current = frozen
      createMutation.mutate(frozen)
    } else {
      updateMutation.mutate(body as UpdateAgentDefBody)
    }
  }

  function retryCreate() {
    if (createRetryTimer.current) {
      globalThis.clearTimeout(createRetryTimer.current)
      createRetryTimer.current = undefined
    }
    const body = frozenCreateBody.current
    if (body) createMutation.mutate(body)
  }

  function retryUpdate() {
    if (updateRetryTimer.current) {
      globalThis.clearTimeout(updateRetryTimer.current)
      updateRetryTimer.current = undefined
    }
    setApiError(null)
    reconcileMutation.mutate()
  }

  function handleSelectTemplate(t: AgentTemplate | null) {
    setAppliedTemplate(t)
    setEditorKey((k) => k + 1)
  }

  const isPending =
    createMutation.isPending ||
    updateMutation.isPending ||
    reconcileMutation.isPending

  const topicOriginLoading =
    !!requestedTopicOrigin &&
    (originTopicQuery.isPending || (!originTopicQuery.isError && !topicOrigin))
  const agentStillLoading = !isNew && agentLoading
  const showSkeleton = useSkeletonDelay(topicOriginLoading || agentStillLoading)

  const title = isNew
    ? agentEditorCopy.form.newAgentTitle
    : (agent?.name ?? agentId ?? agentEditorCopy.form.editAgentTitle)

  const crumbs = [
    { label: wsName, to: ROUTES.home },
    { label: agentEditorCopy.form.agentsCrumb },
    {
      label: isNew
        ? agentEditorCopy.form.newCrumb
        : (agent?.name ?? agentEditorCopy.form.editCrumb),
    },
  ]

  // `isNew` is `!agentId`, so the edit branch always has one.
  const editedId = agentId ?? ''
  const parentPath = isNew
    ? ROUTES.agentNew(wsId)
    : ROUTES.agentEdit(wsId, editedId)
  const promptPath = isNew
    ? ROUTES.agentNewPrompt(wsId)
    : ROUTES.agentEditPrompt(wsId, editedId)
  const parentUrl = withValidatedReturnTo(parentPath, returnTo)
  const promptUrl = withValidatedReturnTo(promptPath, returnTo)

  if (topicOriginLoading) {
    return (
      <WsPageShell crumbs={crumbs} title={agentEditorCopy.form.newAgentTitle}>
        <Stack gap="space-8" aria-busy="true">
          {showSkeleton && <Skeleton lines={3} />}
        </Stack>
      </WsPageShell>
    )
  }

  if (requestedTopicOrigin && (originTopicQuery.isError || !topicOrigin)) {
    return (
      <WsPageShell crumbs={crumbs} title={agentEditorCopy.form.newAgentTitle}>
        <Alert tone="danger">
          <Inline gap="space-8" align="baseline">
            <Text>{agentEditorCopy.form.topicLoadFailed}</Text>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void originTopicQuery.refetch()}
            >
              {agentEditorCopy.retry}
            </Button>
          </Inline>
        </Alert>
      </WsPageShell>
    )
  }

  if (agentStillLoading) {
    return (
      <WsPageShell crumbs={crumbs} title={title}>
        <Stack gap="space-8" aria-busy="true">
          {showSkeleton && <Skeleton lines={3} />}
        </Stack>
      </WsPageShell>
    )
  }

  if (!isNew && !agent) {
    const loadFailed =
      agentIsError &&
      !(agentError instanceof ApiError && agentError.status === 404)
    return (
      <WsPageShell crumbs={crumbs} title={title}>
        {loadFailed ? (
          <Alert tone="danger">
            <Inline gap="space-8" align="baseline">
              <Text>{agentEditorCopy.loadAgentFailed}</Text>
              <Button
                variant="outline"
                size="sm"
                onClick={() => void refetchAgent()}
              >
                {agentEditorCopy.retry}
              </Button>
            </Inline>
          </Alert>
        ) : (
          <Alert tone="danger">{agentEditorCopy.form.notFound}</Alert>
        )}
      </WsPageShell>
    )
  }

  if (isNew && createSubmission) {
    return (
      <WsPageShell crumbs={crumbs} title={title}>
        <Stack gap="space-12">
          <Alert
            tone={
              createSubmission.status === 'pending'
                ? 'info'
                : createSubmission.status === 'unknown'
                  ? 'warning'
                  : 'success'
            }
          >
            <Stack gap="space-4">
              <Text>
                {createSubmission.status === 'pending'
                  ? agentEditorCopy.status.createPendingHeading
                  : createSubmission.status === 'unknown'
                    ? agentEditorCopy.status.createUnknownHeading
                    : agentEditorCopy.status.createdHeading}
              </Text>
            </Stack>
          </Alert>
          {createSubmission.status !== 'confirmed' && (
            <Inline gap="space-8">
              <Button
                variant="primary"
                onClick={retryCreate}
                loading={createMutation.isPending}
              >
                {agentEditorCopy.retry}
              </Button>
              <Button variant="outline" onClick={goBack}>
                {agentEditorCopy.status.returnToCaller}
              </Button>
            </Inline>
          )}
        </Stack>
      </WsPageShell>
    )
  }

  if (isNew && assignmentSubmission) {
    return (
      <WsPageShell crumbs={crumbs} title={title}>
        <Stack gap="space-12">
          <Alert
            tone={
              assignmentSubmission.status === 'assigning' ? 'info' : 'warning'
            }
          >
            <Stack gap="space-4">
              <Text>
                {assignmentSubmission.status === 'assigning'
                  ? agentEditorCopy.status.assigningHeading
                  : agentEditorCopy.status.assignmentUnknownHeading}
              </Text>
            </Stack>
          </Alert>
          {assignmentSubmission.status === 'unknown' && topicOrigin && (
            <Inline gap="space-8">
              <Button
                variant="primary"
                onClick={() => void retryTopicAssignment()}
              >
                {agentEditorCopy.status.retryAssignment}
              </Button>
              <Button
                variant="ghost"
                href={ROUTES.agentProfile(
                  topicOrigin.workspaceId,
                  assignmentSubmission.agent.agent_id,
                )}
                replace
              >
                {agentEditorCopy.status.openProfile}
              </Button>
            </Inline>
          )}
        </Stack>
      </WsPageShell>
    )
  }

  if (!isNew && updatePending) {
    return (
      <WsPageShell crumbs={crumbs} title={title}>
        <Stack gap="space-12">
          <Alert tone="info">
            <Stack gap="space-4">
              <Text>{agentEditorCopy.status.reconcilePendingHeading}</Text>
            </Stack>
          </Alert>
          {apiError && <Alert tone="danger">{apiError}</Alert>}
          <Inline gap="space-8">
            <Button
              variant="primary"
              onClick={retryUpdate}
              loading={reconcileMutation.isPending}
            >
              {agentEditorCopy.retry}
            </Button>
            <Button variant="outline" onClick={goBack}>
              {agentEditorCopy.status.returnToCaller}
            </Button>
          </Inline>
        </Stack>
      </WsPageShell>
    )
  }

  return (
    <AgentFormEditor
      seedKey={editorKey}
      agent={agent}
      appliedTemplate={appliedTemplate}
      crumbs={crumbs}
      title={title}
      isNew={isNew}
      templates={templates}
      templatesLoading={templatesLoading}
      templatesError={templatesError}
      onRetryTemplates={() => void refetchTemplates()}
      onSelectTemplate={handleSelectTemplate}
      providers={providers}
      workspaceId={wsId}
      isPending={isPending}
      error={apiError}
      onSubmit={handleSubmit}
      onCancel={goBack}
      saveTemplateIsPending={saveTemplateMutation.isPending}
      showSaveTemplate={showSaveTemplate}
      onToggleSaveTemplate={() => setShowSaveTemplate((v) => !v)}
      onCloseSaveTemplate={() => setShowSaveTemplate(false)}
      onSaveTemplate={(body) => saveTemplateMutation.mutate(body)}
      parentUrl={parentUrl}
      promptUrl={promptUrl}
      onTogglePause={isNew ? undefined : () => void togglePause()}
      pauseIsPending={pausePending}
    />
  )
}

// ── Form body ──────────────────────────────────────────────────────────────────

/** Handed to the nested system-prompt route via the outlet. */
export interface AgentPromptOutletContext {
  agent: AgentDefInfo | null
  crumbs: Crumb[]
  /** The form page this prompt page belongs to. */
  parentUrl: string
  prompt: string
  setPrompt: (value: string) => void
  /** The prompt differs from the saved agent or the applied template. */
  dirty: boolean
}

/**
 * Owns the form state so it survives the system-prompt page: that page is a
 * *nested* route, so this component stays mounted and hands the prompt down
 * through the outlet context rather than losing the draft to a navigation.
 */
function AgentFormEditor({
  seedKey,
  agent,
  appliedTemplate,
  crumbs,
  title,
  isNew,
  templates,
  templatesLoading,
  templatesError,
  onRetryTemplates,
  onSelectTemplate,
  providers,
  workspaceId,
  isPending,
  error,
  onSubmit,
  onCancel,
  saveTemplateIsPending,
  showSaveTemplate,
  onToggleSaveTemplate,
  onCloseSaveTemplate,
  onSaveTemplate,
  parentUrl,
  promptUrl,
  onTogglePause,
  pauseIsPending,
}: {
  /** Bumped when a template (or Blank) is chosen: reseed the form in place. */
  seedKey: number
  agent: AgentDefInfo | null
  appliedTemplate: AgentTemplate | null
  crumbs: Crumb[]
  title: string
  isNew: boolean
  templates: AgentTemplate[]
  templatesLoading: boolean
  templatesError: boolean
  onRetryTemplates: () => void
  onSelectTemplate: (t: AgentTemplate | null) => void
  providers: AvailableProviderInfo[]
  workspaceId: string
  isPending: boolean
  error: string | null
  onSubmit: (body: CreateAgentDefDraftBody | UpdateAgentDefBody) => void
  onCancel: () => void
  saveTemplateIsPending: boolean
  showSaveTemplate: boolean
  onToggleSaveTemplate: () => void
  onCloseSaveTemplate: () => void
  onSaveTemplate: (body: Parameters<typeof agentTemplatesApi.create>[1]) => void
  parentUrl: string
  promptUrl: string
  /** v2 editor only: lifecycle toggle, submitted separately from config. */
  onTogglePause?: () => void
  pauseIsPending?: boolean
}) {
  const seed = () => {
    if (appliedTemplate) return fromTemplate(appliedTemplate)
    if (agent) return fromDef(agent)
    return null
  }
  const [form, setForm] = useState<FormState>(() => seed() ?? { ...BLANK })
  // Snapshot to diff against: the last-saved agent when editing, or the
  // template a new agent was forked from. A blank new agent has no base —
  // everything would read as "added", which isn't useful — so the diff is
  // hidden in that case. Reseeded in place (not re-mounted) whenever a
  // template is applied, so the template select keeps focus.
  const [base, setBase] = useState<FormState | null>(seed)
  const [seededAt, setSeededAt] = useState(seedKey)
  if (seededAt !== seedKey) {
    const next = seed()
    setSeededAt(seedKey)
    setForm(next ?? { ...BLANK })
    setBase(next)
  }

  // Stable identity: the editor's provider→model effect depends on it.
  const set = useCallback(
    (k: keyof FormState, v: FormState[keyof FormState]) =>
      setForm((f) => ({ ...f, [k]: v })),
    [],
  )

  const outlet = useOutlet({
    agent,
    crumbs,
    parentUrl,
    prompt: form.system_prompt,
    setPrompt: (value: string) => set('system_prompt', value),
    dirty: form.system_prompt !== (base?.system_prompt ?? ''),
  } satisfies AgentPromptOutletContext)
  if (outlet) return outlet

  return (
    <WsPageShell
      crumbs={crumbs}
      title={title}
      description={isNew ? agentEditorCopy.form.newAgentDescription : undefined}
    >
      <AgentV2Editor
        seedKey={seedKey}
        lead={
          isNew ? (
            <TemplateCard
              templates={templates}
              selectedId={appliedTemplate?.id}
              onSelect={onSelectTemplate}
              loading={templatesLoading}
              error={templatesError}
              onRetry={onRetryTemplates}
            />
          ) : undefined
        }
        agent={agent}
        workspaceId={workspaceId}
        providers={providers}
        isPending={isPending}
        error={error}
        onSubmit={onSubmit}
        onCancel={onCancel}
        form={form}
        set={set}
        base={base}
        promptUrl={promptUrl}
        onRequestSaveTemplate={onToggleSaveTemplate}
        onTogglePause={onTogglePause}
        pauseIsPending={pauseIsPending}
      />
      {showSaveTemplate && (
        <SaveTemplateSection
          key={seedKey}
          initialName={agent?.name ?? appliedTemplate?.name ?? ''}
          isPending={saveTemplateIsPending}
          onSave={(name, description) =>
            onSaveTemplate({
              ...toTemplateBody(form),
              name,
              description: description || undefined,
            })
          }
          onClose={onCloseSaveTemplate}
        />
      )}
    </WsPageShell>
  )
}
